import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { ArrowLeft } from '@phosphor-icons/react';
import ProductSearchSection from './newOrder/ProductSearchSection';
import AddressEditor from '@/components/AddressEditor';
import { validateCheckout } from '@/shared/checkoutValidation';
import { inputCls } from './newOrder/styles';
import useCart from './newOrder/useCart';
import useAddresses from './newOrder/useAddresses';
import usePaymentMethods from './newOrder/usePaymentMethods';
import useCheckoutVerify from './newOrder/useCheckoutVerify';
import useDiscounts from './newOrder/useDiscounts';
import CartSection from './newOrder/CartSection';
import AddressesSection from './newOrder/AddressesSection';
import DiscountsSection from './newOrder/DiscountsSection';
import PaymentSection from './newOrder/PaymentSection';
import TotalBox from './newOrder/TotalBox';
import OrderConfirmedScreen from './newOrder/OrderConfirmedScreen';
import { validateMbwayPhone } from '@/utils/mbwayValidation';

function validateExtraCheckout({ isInstallments, parcelas, maxParcelas, isPhonePayment, mbwayPhone }) {
  if (isInstallments) {
    const n = Number(parcelas || 0);
    if (!Number.isInteger(n) || n < 1 || n > maxParcelas) {
      return `Escolha um número de parcelas entre 1 e ${maxParcelas}.`;
    }
  }
  if (isPhonePayment) {
    return validateMbwayPhone(mbwayPhone);
  }
  return null;
}

export default function NewOrderPage() {
  const navigate = useNavigate();

  // Product search (kept inline as it's tightly coupled to the search section UI)
  const [productIdSearch, setProductIdSearch] = useState('');
  const [productNameSearch, setProductNameSearch] = useState('');
  const [products, setProducts] = useState([]);
  const [buyerType, setBuyerType] = useState('CF');
  const [productQuantities, setProductQuantities] = useState({});
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [searchTriggered, setSearchTriggered] = useState(false);

  // Address editor modal
  const [addressEditor, setAddressEditor] = useState(null);

  // UI toggles
  const [observacoes, setObservacoes] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [showAddPhone, setShowAddPhone] = useState(false);
  const [newPhone, setNewPhone] = useState('');

  // Confirm
  const [confirming, setConfirming] = useState(false);
  const [orderResult, setOrderResult] = useState(null);

  // Hooks
  const cartH = useCart();
  const addr = useAddresses();
  const pay = usePaymentMethods();
  const disc = useDiscounts({
    selectedTransport: pay.selectedTransport,
    selectedPayment: pay.selectedPayment,
    selectedEnvio: addr.selectedEnvio,
  });
  const verify = useCheckoutVerify({
    cartLength: cartH.cart.length,
    selectedTransport: pay.selectedTransport,
    selectedPayment: pay.selectedPayment,
    selectedEnvio: addr.selectedEnvio,
    appliedCreditos: disc.appliedCreditos,
    appliedVales: disc.appliedVales,
    appliedPromo: disc.appliedPromo,
    balances: disc.balances,
  });

  // ─── Product search actions ───
  const searchProducts = async () => {
    setSearchTriggered(true);
    setLoadingProducts(true);
    try {
      const { data } = await api.post('/encomendas/searchProducts', {
        product_id: productIdSearch, product_name: productNameSearch,
      });
      if (data.success) {
        setProducts(data.data || []);
        setBuyerType(data.id_tipo_cliente || 'CF');
      } else {
        setProducts([]);
      }
    } catch (e) {
      console.error('searchProducts failed', e);
      setProducts([]);
    } finally {
      setProductQuantities({});
      setLoadingProducts(false);
    }
  };

  const setQty = (id, val) => setProductQuantities((prev) => ({
    ...prev, [id]: Math.max(0, parseInt(val, 10) || 0),
  }));

  const onAddProductToCart = async (prod) => {
    const qty = productQuantities[prod.ID_Produto] || 0;
    if (qty < 1) return;
    await cartH.add(prod.ID_Produto, qty);
    setProductQuantities((prev) => ({ ...prev, [prod.ID_Produto]: 0 }));
  };

  const onSavePhone = async () => {
    const r = await pay.addPhone(newPhone);
    if (r.ok) {
      setNewPhone(''); setShowAddPhone(false);
    } else if (r.message) {
      // eslint-disable-next-line no-alert
      alert(r.message);
    }
  };

  const handleConfirm = async () => {
    const check = validateCheckout({
      verifyData: verify.spTotal,
      billingAddressId: addr.selectedFatura || addr.selectedEnvio,
      shippingAddressId: addr.selectedEnvio,
      deliveryMethod: pay.selectedTransport, deliveryMethods: pay.transportMethods,
      paymentMethod: pay.selectedPayment, paymentMethods: pay.paymentMethods,
      mbwayPhone: pay.mbwayPhone, cartEmpty: cartH.cart.length === 0,
    });
    if (!check.ok) { window.alert(check.message); return; }

    const extraErr = validateExtraCheckout({
      isInstallments: pay.isInstallments, parcelas: pay.parcelas, maxParcelas: pay.maxParcelas,
      isPhonePayment: pay.isPhonePayment, mbwayPhone: pay.mbwayPhone,
    });
    if (extraErr) { window.alert(extraErr); return; }

    setConfirming(true);
    try {
      const { data } = await api.post('/encomendas/confirm', {
        id_tipo_transporte: pay.selectedTransport,
        id_tipo_pagamento: pay.selectedPayment,
        id_morada_envio: addr.selectedEnvio,
        id_morada_fatura: addr.selectedFatura || addr.selectedEnvio,
        observacoes,
        desconto_vale: disc.appliedVales,
        desconto_creditos: disc.appliedCreditos,
        codigo_promocional: disc.appliedPromo,
        id_portes: verify.spTotal?.ID_Portes || '',
        parcelas: pay.isInstallments ? pay.parcelas : 0,
        numero_telemovel: pay.isPhonePayment ? pay.mbwayPhone : '',
      });
      setOrderResult(data);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.warn('confirm failed', e);
      setOrderResult({ success: false, message: 'Erro de comunicação.' });
    } finally {
      setConfirming(false);
    }
  };

  // Reset MBWay/parcelas when payment method changes (legacy parity)
  const onChangePayment = (id) => {
    pay.setSelectedPayment(id);
    pay.setMbwayPhone('');
    pay.setParcelas(1);
    setShowAddPhone(false);
  };

  if (orderResult?.success) {
    return <OrderConfirmedScreen message={orderResult.message} onBack={() => navigate('/encomendas')} />;
  }

  return (
    <div data-testid="new-order-page">
      <div className="mb-6">
        <button onClick={() => navigate('/encomendas')} data-testid="back-btn"
          className="flex items-center gap-1 text-sm text-[#6B7280] hover:text-[#0F1829] mb-2 transition-colors">
          <ArrowLeft size={16} /> Voltar às Encomendas
        </button>
        <h1 className="text-2xl font-bold text-[#111827]">Carrinho</h1>
        <p className="text-sm text-[#6B7280] mt-1">Adicione produtos ao carrinho e finalize a encomenda</p>
      </div>

      <ProductSearchSection
        productIdSearch={productIdSearch} setProductIdSearch={setProductIdSearch}
        productNameSearch={productNameSearch} setProductNameSearch={setProductNameSearch}
        onSearch={searchProducts} loadingProducts={loadingProducts}
        searchTriggered={searchTriggered} products={products} buyerType={buyerType}
        productQuantities={productQuantities} onSetQty={setQty}
        onAddToCart={onAddProductToCart}
      />

      <CartSection
        cart={cartH.cart} loading={cartH.loading}
        total={cartH.total} netPoints={cartH.netPoints}
        onUpdateQty={cartH.updateQty} onRemove={cartH.remove} onClear={cartH.clearAll}
      />

      {cartH.cart.length > 0 && (
        <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm" data-testid="section-checkout">
          <div className="px-5 py-3 border-b border-[#E5E7EB] bg-[#F9FAFB] rounded-t-xl">
            <h2 className="text-sm font-semibold text-[#111827]">Encomendar</h2>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-7 space-y-4">
                <AddressesSection
                  addresses={addr.addresses} billingAddresses={addr.billingAddresses}
                  selectedFatura={addr.selectedFatura} setSelectedFatura={addr.setSelectedFatura}
                  selectedTransport={pay.selectedTransport} setSelectedTransport={pay.setSelectedTransport}
                  transportMethods={pay.transportMethods}
                  selectedEnvio={addr.selectedEnvio} setSelectedEnvio={addr.setSelectedEnvio}
                  onOpenEditor={setAddressEditor}
                />

                <DiscountsSection
                  promoCode={disc.promoCode} setPromoCode={disc.setPromoCode}
                  appliedPromo={disc.appliedPromo} applyPromo={disc.applyPromo} removePromo={disc.removePromo}
                  promoLoading={disc.promoLoading} promoError={disc.promoError}
                  promoSuccess={disc.promoSuccess} setPromoError={disc.setPromoError}
                  spTotal={verify.spTotal} spValorDescontoCP={verify.spValorDescontoCP}
                  valesValue={disc.valesValue} setValesValue={disc.setValesValue}
                  appliedVales={disc.appliedVales} applyVales={disc.applyVales} removeVales={disc.removeVales}
                  spValesPermitidos={verify.spValesPermitidos} spValesExistentes={verify.spValesExistentes}
                  spDescontoVale={verify.spDescontoVale}
                  creditosValue={disc.creditosValue} setCreditosValue={disc.setCreditosValue}
                  appliedCreditos={disc.appliedCreditos} applyCreditos={disc.applyCreditos}
                  removeCreditos={disc.removeCreditos}
                  spCreditosPermitidos={verify.spCreditosPermitidos}
                  spCreditosExistentes={verify.spCreditosExistentes}
                  spDescontoCreditos={verify.spDescontoCreditos}
                />

                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Observações</label>
                  <textarea data-testid="order-notes" value={observacoes}
                    onChange={(e) => setObservacoes(e.target.value)}
                    rows={3} placeholder="Observações (opcional)" className={`${inputCls} resize-y`} />
                </div>
              </div>

              <div className="lg:col-span-5 space-y-4">
                <PaymentSection
                  selectablePaymentMethods={pay.selectablePaymentMethods}
                  selectedPayment={pay.selectedPayment} onChangePayment={onChangePayment}
                  isInstallments={pay.isInstallments} parcelas={pay.parcelas}
                  setParcelas={pay.setParcelas} maxParcelas={pay.maxParcelas}
                  isPhonePayment={pay.isPhonePayment}
                  phoneNumbers={pay.phoneNumbers} mbwayPhone={pay.mbwayPhone}
                  setMbwayPhone={pay.setMbwayPhone}
                  showAddPhone={showAddPhone} setShowAddPhone={setShowAddPhone}
                  newPhone={newPhone} setNewPhone={setNewPhone} onSavePhone={onSavePhone}
                />

                <TotalBox
                  showDetails={showDetails} setShowDetails={setShowDetails}
                  spSubtotal={verify.spSubtotal} spPortes={verify.spPortes} spIva={verify.spIva}
                  cartTotal={cartH.total}
                  spValorDescontoCP={verify.spValorDescontoCP}
                  spDescontoVale={verify.spDescontoVale}
                  spDescontoCreditos={verify.spDescontoCreditos}
                  spDescontos={verify.spDescontos}
                  spTotalPagar={verify.spTotalPagar}
                  verifying={verify.verifying}
                  confirming={confirming} onConfirm={handleConfirm}
                  isPhonePayment={pay.isPhonePayment} mbwayPhone={pay.mbwayPhone}
                  selectedPayment={pay.selectedPayment} selectedTransport={pay.selectedTransport}
                  selectedAddrEnvio={addr.selectedEnvio}
                  orderResult={orderResult}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {addressEditor && (
        <AddressEditor
          mode={addressEditor.mode}
          tipoMorada={addressEditor.tipoMorada}
          address={addressEditor.address}
          onClose={() => setAddressEditor(null)}
          onSaved={(r) => {
            const newId = r?.ID_ClienteMorada || addressEditor.address?.ID_ClienteMorada;
            addr.reload(newId, addressEditor.target);
          }}
        />
      )}
    </div>
  );
}
